import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Voucher, VoucherStatus, DiscountType } from '@/services/admin/vouchers/vouchers.interfaces';
import { format } from 'date-fns';
import { Edit, Power, PowerOff, Trash2 } from 'lucide-react';

interface VoucherCardProps {
  voucher: Voucher;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  onActivate?: (id: string) => void;
  onDeactivate?: (id: string) => void;
  isSelected?: boolean;
  onSelect?: (id: string, selected: boolean) => void;
  showActions?: boolean;
}

const getStatusColor = (status: VoucherStatus) => {
  switch (status) {
    case 'Active':
      return 'bg-green-100 text-green-800';
    case 'Inactive':
      return 'bg-gray-100 text-gray-800';
    case 'Expired':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const getDiscountTypeLabel = (type: DiscountType) => {
  switch (type) {
    case 'percentage':
      return '%';
    case 'fixedAmount':
      return 'R$';
    case 'freeShipping':
      return 'Frete Grátis';
    default:
      return '';
  }
};

const formatDate = (dateString: string) => {
  try {
    return format(new Date(dateString), 'dd/MM/yyyy');
  } catch {
    return dateString;
  }
};

export const VoucherCard: React.FC<VoucherCardProps> = ({
  voucher,
  onEdit,
  onDelete,
  onActivate,
  onDeactivate,
  isSelected = false,
  onSelect,
  showActions = true,
}) => {
  const [showDeactivateDialog, setShowDeactivateDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const handleSelect = () => {
    onSelect?.(voucher.id, !isSelected);
  };

  const usagePercentage = voucher.maxUses 
    ? Math.round((voucher.currentUses / voucher.maxUses) * 100)
    : 0;

  return (
    <Card className={`relative ${isSelected ? 'ring-2 ring-blue-500' : ''}`}>
      {onSelect && (
        <div className="absolute top-2 left-2">
          <label className="sr-only">Selecionar voucher</label>
          <input
            type="checkbox"
            checked={isSelected}
            onChange={handleSelect}
            aria-label={`Selecionar voucher ${voucher.name}`}
            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
          />
        </div>
      )}
      
      <CardHeader className="pb-2">
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <CardTitle className="text-lg font-semibold">{voucher.name}</CardTitle>
            <p className="text-sm text-gray-600 font-mono">{voucher.code}</p>
          </div>
          <Badge className={getStatusColor(voucher.status)}>
            {voucher.status}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        {voucher.description && (
          <p className="text-sm text-gray-600">{voucher.description}</p>
        )}

        <div className="grid grid-cols-2 gap-2 text-sm">
          <div>
            <span className="font-medium">Desconto:</span>
            <div className="flex items-center gap-1">
              {voucher.discountType === 'freeShipping' ? (
                <span>Frete Grátis</span>
              ) : (
                <>
                  <span>{voucher.discountValue}</span>
                  <span>{getDiscountTypeLabel(voucher.discountType)}</span>
                </>
              )}
            </div>
          </div>
          
          <div>
            <span className="font-medium">Uso:</span>
            <div>
              {voucher.currentUses}/{voucher.maxUses || '∞'} ({usagePercentage}%)
            </div>
          </div>

          <div>
            <span className="font-medium">Válido até:</span>
            <div>{formatDate(voucher.validUntil)}</div>
          </div>

          <div>
            <span className="font-medium">Por usuário:</span>
            <div>{voucher.maxUsesPerUser || '∞'}</div>
          </div>
        </div>

        {voucher.packageRestrictions && (
          <div className="text-xs text-gray-500">
            <span className="font-medium">Pacotes:</span> {voucher.packageRestrictions.type === 'include' ? 'Incluir' : 'Excluir'} {voucher.packageRestrictions.packageIds.length}
          </div>
        )}

        {showActions && (
          <div className="flex gap-2 pt-2">
            {onEdit && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onEdit(voucher.id)}
                className="flex-1"
              >
                <Edit className="w-4 h-4 mr-2" />
                Editar
              </Button>
            )}
            
            {voucher.isActive && onDeactivate ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowDeactivateDialog(true)}
                className="flex-1"
              >
                <PowerOff className="w-4 h-4 mr-2" />
                Desativar
              </Button>
            ) : !voucher.isActive && onActivate ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onActivate(voucher.id)}
                className="flex-1"
              >
                <Power className="w-4 h-4 mr-2" />
                Ativar
              </Button>
            ) : null}

            {onDelete && (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setShowDeleteDialog(true)}
                className="flex-1"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Excluir
              </Button>
            )}
          </div>
        )}
      </CardContent>

      {/* Diálogos de Confirmação */}
      <ConfirmDialog
        open={showDeactivateDialog}
        onOpenChange={setShowDeactivateDialog}
        title="Desativar Voucher"
        description={`Tem certeza que deseja desativar o voucher "${voucher.name}"? Ele não poderá mais ser usado pelos usuários.`}
        confirmText="Desativar"
        cancelText="Cancelar"
        variant="default"
        onConfirm={() => onDeactivate?.(voucher.id)}
      />

      <ConfirmDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        title="Excluir Voucher"
        description={`Tem certeza que deseja excluir o voucher "${voucher.name}"? Esta ação não pode ser desfeita.`}
        confirmText="Excluir"
        cancelText="Cancelar"
        variant="destructive"
        onConfirm={() => onDelete?.(voucher.id)}
      />
    </Card>
  );
};
